'use es6';

import { getWidgetByKeyAsync } from 'reporting-reports/library';
import { capturePageEvent, FETCH_REPORT_KEY } from '../sentry/sentry-page-events';
import { fromJS, Map as ImmutableMap } from 'immutable';
import { REPORT_KEY_TO_CONFIG, REALM_REPORT_TO_IN_DEV_REPORT_GATE_CONFIG, IN_DEV_REPORT_TO_REALM_REPORT_GATE_CONFIG } from '../constants/inDevReports';
import { getTemplateWithMetricEditor } from '../constants/inDevReportMetricEditorSchemas';
import { getReportTemplateByKey } from '../data/reportTemplatesDao';
import { getReportWithCustomKpiEditorSchema } from './editorSchemaHelpers';
export const templatesBySubAppKey = ImmutableMap({
  // SALES ANALYTICS - ACTIVITIES AND LEADS
  activities: 'sales-analytics.activities',
  activityStream: 'sales-analytics.team-activity-timeline',
  callOutcomes: 'sales-analytics.call-outcomes',
  calls: 'calls-performance',
  conversations: 'sales-analytics.chats',
  emails: 'sales-analytics.emails-performance',
  leads: 'leads-progress',
  leadStages: 'lead-journey-progress',
  leadResponseTime: 'sales-analytics.lead-response-time',
  meetingOutcomes: 'sales-analytics.meeting-outcomes',
  meetings: 'meetings-performance',
  tasks: 'tasks-performance',
  taskOutcomes: 'sales-analytics.tasks-completed',
  teamActivity: 'team-activity',
  sequences: 'sequences-performance',
  emailRates: 'email-rates',
  // SALES ANALYTICS - DEALS
  averageSalesPrice: 'sales-analytics.average-deal-size',
  changeHistoryDealList: 'sales-analytics.deal-change-history',
  dealJourney: 'deal-journey-progress',
  dealPushAnalysis: 'sales-analytics.deal-push-rate',
  dealsCreatedByRep: 'sales-analytics.deals-created',
  dealsWon: 'sales-analytics.deals-won-lost',
  dealVelocity: 'sales-analytics.deal-velocity',
  lossReasons: 'sales-analytics.deal-loss-reasons',
  salesVelocity: 'sales-velocity',
  timeInDealStage: 'sales-analytics.time-spent-in-deal-stage',
  waterfall: 'sales-analytics.waterfall',
  // SALES ANALYTICS - FORECAST AND PIPELINES
  manualForecast: 'sales-analytics.forecast-category-using-weighted-amount',
  manualForecastTotal: 'sales-analytics.forecast-category-using-total-amount',
  pipelineSnapshots: 'sales-analytics.pipeline-snapshots',
  quotaAttainment: 'sales-analytics.quota-attainment',
  salesBySource: 'sales-analytics.deal-revenue-by-source',
  weightedPipelineForecast: 'sales-analytics.weighted-pipeline-forecast',
  // SALES ANALYTICS - LINE ITEM RECURRING REVENUE
  closedWonRecurringRevenue: 'sales-analytics.closed-won-recurring-revenue',
  openRecurringRevenue: 'sales-analytics.open-recurring-revenue',
  allClosedWonRevenue: 'sales-analytics.all-closed-won-revenue',
  // SERVICE REPORTS - CUSTOMER SATISFACTION
  postSupportCSAT: 'service-analytics.customer-satisfaction-score',
  customerEffortScore: 'service-analytics.ces-average-over-time',
  // SERVICE REPORTS - TEAM PERFORMANCE
  ticketEnteredNewTimeOfDay: 'hour-of-day---tickets-entered-status-"new"',
  ticketEnteredNewDayOfWeek: 'day-of-week---tickets-entered-status-"new"',
  ticketEnteredWaitingTimeOfDay: 'hour-of-day---tickets-entered-status-"waiting-on-us"',
  ticketEnteredWaitingDayOfWeek: 'day-of-week---tickets-entered-status-"waiting-on-us"',
  ticketTimeInPipeline: 'service-analytics.ticket-time-in-pipeline',
  supportVolume: 'service-analytics.support-volume',
  queueTime: 'service-analytics.chat-wait-time',
  ticketTimeToCloseAllHours: 'service-analytics.resolution-time',
  ticketTimeToCloseSLAHours: 'ticket-average-time-to-close-in-sla-hours-by-rep',
  ticketTimeToRespondAllHours: 'service-team-performance-v2.ticketResponseTimeByAssignee',
  ticketTimeToRespondSLAHours: 'ticket-average-time-to-first-response-in-sla-hours-by-rep',
  // SERVICE REPORTS - TICKET REPLIES
  averageCustomerReplyTime: 'service-analytics.average-customer-reply-time',
  customersWithHighestAverageReplyTime: 'service-analytics.customers-with-highest-average-reply-time',
  customerWaitTime: 'service-analytics.average-customer-wait-time',
  ownerReplyCount: 'service-analytics.owner-reply-count',
  averageReplyTime: 'service-analytics.average-owner-reply-time',
  averageReplyTimeByOwner: 'service-analytics.average-reply-time-by-owner',
  ticketsWithMostMessagesSent: 'service-analytics.tickets-with-most-owner-replies',
  ticketsWithMostMessagesReceived: 'service-analytics.tickets-with-the-most-customer-replies',
  messagesSentByTimeOfDay: 'service-analytics.total-owner-replies-by-time-of-day',
  messagesReceivedByTimeOfDay: 'service-analytics.total-customer-replies-by-time-of-day',
  // SERVICE REPORTS - PLANNING
  workingHoursAvailability: 'service-analytics-working-hours-availability',
  maxAvailableRep: 'service-analytics-maximum-available-rep',
  repAvailabilityTopLevel: 'service-analytics-rep-availability-top-level',
  repAvailabilityDrilldown: 'service-analytics-rep-availability-drill-down',
  // SERVICE REPORTS - SELF SERVICE PERFORMANCE
  knowledgeBaseToTickets: 'journeys.knowledgeBaseArticlesToTickets',
  chatbotAndCustomerAgentHandoffs: 'journeys.chatbotAndCustomerAgentHandoffs',
  chatbotHandoffs: 'journeys.chatbotHandoffs',
  ticketsToDeals: 'journeys.ticketsToDeals',
  // SUBSCRIPTION REPORTS
  newMonthlyRecurringRevenue: 'subscription-analytics.new-subscriptions-mrr',
  newAnnualRecurringRevenue: 'subscription-analytics.new-subscriptions-arr',
  churnMonthlyRecurringRevenue: 'subscription-analytics.churn-subscriptions-mrr',
  churnAnnualRecurringRevenue: 'subscription-analytics.churn-subscriptions-arr',
  churnSubscriptions: 'subscription-reports.churn-subscriptions',
  newSubscriptions: 'subscription-analytics.new-subscriptions',
  //MARKETING REPORTS - CHANNEL PERFORMANCE
  adNetworks: 'marketing-analytics.ad-clicks-by-network',
  adCampaigns: 'marketing-analytics.ad-clicks-by-campaign',
  forms: 'marketing-analytics.form-submissions-by-form',
  marketingEmails: 'marketing-analytics.marketing-email-clicks-by-email',
  sms: 'sms-collection-report',
  cta: 'marketing-analytics.cta',
  social: 'social-report.audience',
  //MARKETING REPORTS - ADVANCED REPORTING
  dealStageProgression: 'journeys.dealStageProgression',
  lifecycleStageProgression: 'journeys.lifecycleStageProgression',
  pageVisitsToFormSubmissions: 'journeys.pageVisitsToFormSubmissions',
  leadCreationBrokenDownByAdNetwork: 'journeys.leadCreationBrokenDownByAdNetwork',
  meetingsThatGeneratedDeals: 'journeys.meetingsThatGeneratedDeals',
  //MARKETING REPORTS - WEB TRAFFIC ANALYSIS
  sources: 'marketing-analytics.sessions-by-source',
  pages: 'pages-chart',
  utmCampaign: 'marketing-analytics.utm-campaigns-chart',
  utmSource: 'marketing-analytics.utm-sources-chart',
  utmMedium: 'marketing-analytics.utm-mediums-chart',
  utmContent: 'marketing-analytics.utm-contents-chart',
  utmTerm: 'marketing-analytics.utm-terms-chart',
  deviceTypes: 'marketing-analytics.sessions-by-device-type-over-time',
  geolocation: 'marketing-analytics.sessions-by-country-over-time',
  browsers: 'marketing-analytics.sessions-by-browser-over-time',
  topicClusters: 'topic-clusters-chart',
  revenueAttribution: 'revenue-attribution',
  //MARKETING REPORTS - CONTACT INSIGHTS
  contactInsights: 'marketing-analytics-contact-report-chart',
  // COMMERCE REPORTS - INVOICE REPORTS
  agingSummary: 'commerce-analytics.aging-summary',
  balanceDue: 'commerce-analytics.balance-due-by-billing-type',
  expectedVsActualRevenue: 'commerce-analytics.expected-vs-actual-revenue',
  invoiceStatus: 'commerce-analytics.invoice-status',
  // COMMERCE REPORTS - SUBSCRIPTION REPORTS
  commerceChurnSubscriptions: 'commerce-analytics.churn-subscriptions',
  commerceChurnSubscriptionsCount: 'commerce-analytics.count-churn-subscriptions',
  commerceNewSubscriptions: 'commerce-analytics.new-subscriptions',
  commerceNewSubscriptionsCount: 'commerce-analytics.count-new-subscriptions',
  // COMMERCE REPORTS - PAYMENT REPORTS
  grossPaymentRevenue: 'commerce-analytics.gross-payment-revenue',
  totalPaymentVolume: 'commerce-analytics.total-payment-volume',
  paymentCustomers: 'commerce-analytics.payment-customers'
});
export const subAppKeysByTemplateKey = templatesBySubAppKey.flip();
const editorSchemaMutators = [getTemplateWithMetricEditor, getReportWithCustomKpiEditorSchema];
const getTemplateWithEditors = (report, reportKey, gates) => editorSchemaMutators.reduce((updatedReport, mutator) => mutator(updatedReport, reportKey, gates), report);
export const getUngatedInDevReportForSubAppKey = (subAppKey, gates) => {
  const gateForInDevSubApp = REALM_REPORT_TO_IN_DEV_REPORT_GATE_CONFIG[subAppKey];
  const gateForRealmSubApp = IN_DEV_REPORT_TO_REALM_REPORT_GATE_CONFIG[subAppKey];
  const useRealmReport = gateForRealmSubApp && gates.includes(gateForRealmSubApp);
  if (!useRealmReport && REPORT_KEY_TO_CONFIG[subAppKey] && (!gateForInDevSubApp || gates.includes(gateForInDevSubApp))) {
    return REPORT_KEY_TO_CONFIG[subAppKey];
  } else {
    return null;
  }
};
export const getReportTemplate = (reportKey, gates) => {
  const ungatedInDevReport = getUngatedInDevReportForSubAppKey(reportKey, gates);
  if (ungatedInDevReport) {
    return Promise.resolve(getTemplateWithEditors(fromJS(ungatedInDevReport), reportKey, gates));
  }
  const templateKey = templatesBySubAppKey.get(reportKey);
  if (templateKey) {
    return getReportTemplateByKey(templateKey).then(template => getTemplateWithEditors(template, reportKey, gates));
  }
  capturePageEvent(FETCH_REPORT_KEY, {
    reportKey
  });
  return getWidgetByKeyAsync(reportKey); // fallback that shouldn't be hit
};